import React, { useState, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  Snackbar,
  Alert,
} from "@mui/material";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase"; // Adjust this path as needed
import SupervisorPasscodeManagerAutoClear from "./SupervisorPasscodeManagerAutoclear";

function AccessControl() {
  // 1) Existing states (cardNumber, doorFlags, expiry, etc.)
  const [doorNumber, setDoorNumber] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [userName, setUserName] = useState("");
  const [doorFlags, setDoorFlags] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [indexNumber, setIndexNumber] = useState("");
  const [result, setResult] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // 2) For putCard
  const [putCardNumber, setPutCardNumber] = useState("");
  const [putUserName, setPutUserName] = useState("");
  const [putFrom, setPutFrom] = useState("");
  const [putTo, setPutTo] = useState("");
  const [putDoorFlags, setPutDoorFlags] = useState("");
  const [putPin, setPutPin] = useState("");

  // 3) For getCard
  const [getCardNumber, setGetCardNumber] = useState("");

  // 4) Supervisor passcodes (separate fields).
  const [supervisorDoorNumber, setSupervisorDoorNumber] = useState("");
  const [supervisorPass1, setSupervisorPass1] = useState("");
  const [supervisorPass2, setSupervisorPass2] = useState("");
  const [supervisorPass3, setSupervisorPass3] = useState("");
  const [supervisorPass4, setSupervisorPass4] = useState("");

  // Helper to show snackbars
  const showSnackbar = useCallback((message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  }, []);

  const handleSnackbarClose = useCallback(() => {
    setSnackbarOpen(false);
  }, []);

  // Generic function to call Firebase callable
  const callEndpoint = useCallback(
    async (fnName, params = {}) => {
      try {
        const fn = httpsCallable(functions, fnName);
        const response = await fn(params);
        setResult(JSON.stringify(response.data, null, 2));
        showSnackbar(`Success: ${fnName} called`);
      } catch (error) {
        console.error("Error calling endpoint:", error);
        setResult(`Error: ${error.message}`);
        showSnackbar(`Error: ${error.message}`);
      }
    },
    [showSnackbar]
  );

  //----------------------------------------------------------------
  // Existing handlers for door opening, permission, etc.
  //----------------------------------------------------------------

  const handleOpenDoor = useCallback(() => {
    callEndpoint("openDoor", { doorNumber: parseInt(doorNumber, 10) });
  }, [doorNumber, callEndpoint]);

  const handleGetTotalPermissions = useCallback(() => {
    callEndpoint("totalPermissions");
  }, [callEndpoint]);

  const handleAddPermission = useCallback(() => {
    if (!cardNumber || !userName) {
      showSnackbar("Please enter cardNumber and userName to add permission.");
      return;
    }
    callEndpoint("addPermission", {
      cardNumber: parseInt(cardNumber, 10),
      userName,
      doorFlags,
      expiryDate,
    });
  }, [cardNumber, userName, doorFlags, expiryDate, callEndpoint, showSnackbar]);

  const handleDeleteAllPermissions = useCallback(() => {
    callEndpoint("deleteAllPermissions");
  }, [callEndpoint]);

  const handleDeletePermission = useCallback(() => {
    if (!cardNumber) {
      showSnackbar("Please enter a cardNumber to delete permission.");
      return;
    }
    callEndpoint("deletePermission", { cardNumber: parseInt(cardNumber, 10) });
  }, [cardNumber, callEndpoint, showSnackbar]);

  const handleGetLastSwipedCard = useCallback(() => {
    callEndpoint("lastSwipedCard");
  }, [callEndpoint]);

  const handleSyncDateTime = useCallback(() => {
    callEndpoint("syncDateTime");
  }, [callEndpoint]);

  const handleReadNameByCard = useCallback(() => {
    if (!cardNumber) {
      showSnackbar("Please enter a cardNumber for readNameByCard.");
      return;
    }
    callEndpoint("readNameByCard", { cardNumber: parseInt(cardNumber, 10) });
  }, [cardNumber, callEndpoint, showSnackbar]);

  const handleReadNameByIndex = useCallback(() => {
    if (!indexNumber) {
      showSnackbar("Please enter an indexNumber for readNameByIndex.");
      return;
    }
    callEndpoint("readNameByIndex", { index: parseInt(indexNumber, 10) });
  }, [indexNumber, callEndpoint, showSnackbar]);

  const handleGetAllRecordSwipes = useCallback(() => {
    callEndpoint("allRecordSwipes");
  }, [callEndpoint]);

  const handleViewPermissions = useCallback(() => {
    if (!cardNumber) {
      showSnackbar("Please enter a cardNumber for View Permissions.");
      return;
    }
    callEndpoint("permissions", { cardNumber: parseInt(cardNumber, 10) });
  }, [cardNumber, callEndpoint, showSnackbar]);

  const handleSearchControllers = useCallback(() => {
    callEndpoint("searchControllers");
  }, [callEndpoint]);

  //----------------------------------------------------------------
  // putCard handlers
  //----------------------------------------------------------------

  const handlePutCard = useCallback(() => {
    if (!putCardNumber || !putUserName || !putFrom || !putTo || !putDoorFlags || !putPin) {
      showSnackbar("Please fill in all fields for Put Card.");
      return;
    }
    callEndpoint("putCard", {
      cardNumber: parseInt(putCardNumber, 10),
      userName: putUserName,
      from: putFrom,
      to: putTo,
      doorFlags: putDoorFlags,
      pin: parseInt(putPin, 10),
    });
  }, [putCardNumber, putUserName, putFrom, putTo, putDoorFlags, putPin, callEndpoint, showSnackbar]);

  const handleGetCardAndName = useCallback(async () => {
    if (!getCardNumber) {
      showSnackbar("Please enter a card number for Get Card and Name.");
      return;
    }

    try {
      // 1) getCard
      const getCardFn = httpsCallable(functions, "getCard");
      const getCardResponse = await getCardFn({ cardNumber: parseInt(getCardNumber, 10) });
      if (!getCardResponse.data.successfull) {
        setResult(JSON.stringify(getCardResponse.data, null, 2));
        showSnackbar("Failed to get card info");
        return;
      }

      // 2) readNameByCard
      const readNameFn = httpsCallable(functions, "readNameByCard");
      const readNameResponse = await readNameFn({ cardNumber: parseInt(getCardNumber, 10) });

      const combined = { ...getCardResponse.data };
      if (readNameResponse.data.successfull) {
        const name = readNameResponse.data.value || "";
        if (combined.value) {
          combined.value = { ...combined.value, name };
        }
        combined.info = `${combined.info} + Name retrieved`;
      } else {
        combined.info = `${combined.info} (Name not retrieved)`;
      }

      setResult(JSON.stringify(combined, null, 2));
      showSnackbar("Successfully got card and name!");
    } catch (error) {
      console.error("Error in handleGetCardAndName:", error);
      setResult(`Error: ${error.message}`);
      showSnackbar(`Error: ${error.message}`);
    }
  }, [getCardNumber, showSnackbar]);

  //----------------------------------------------------------------
  // Supervisor passcodes (4 fields)
  //----------------------------------------------------------------

  const handleSetSupervisorPasscodes = useCallback(() => {
    if (!supervisorDoorNumber) {
      showSnackbar("Please enter a door number for supervisor passcodes.");
      return;
    }
    // Convert each pass field into int (default zero if empty)
    const pass1 = parseInt(supervisorPass1.trim() || "0", 10) || 0;
    const pass2 = parseInt(supervisorPass2.trim() || "0", 10) || 0;
    const pass3 = parseInt(supervisorPass3.trim() || "0", 10) || 0;
    const pass4 = parseInt(supervisorPass4.trim() || "0", 10) || 0;

    const passcodes = [pass1, pass2, pass3, pass4];
    callEndpoint("setSupervisorPasscodes", {
      doorNumber: parseInt(supervisorDoorNumber, 10),
      passcodes,
    });
  }, [
    supervisorDoorNumber,
    supervisorPass1,
    supervisorPass2,
    supervisorPass3,
    supervisorPass4,
    callEndpoint,
    showSnackbar,
  ]);

  const handleClearSupervisorPasscodes = useCallback(() => {
    if (!supervisorDoorNumber) {
      showSnackbar("Please enter a door number to clear supervisor passcodes.");
      return;
    }
    callEndpoint("clearSupervisorPasscodes", {
      doorNumber: parseInt(supervisorDoorNumber, 10),
    });
  }, [supervisorDoorNumber, callEndpoint, showSnackbar]);

  //----------------------------------------------------------------
  // Render
  //----------------------------------------------------------------

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Access Control
      </Typography>

      {/* 1) Open Door */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6">Open Door</Typography>
        <TextField
          label="Door Number"
          value={doorNumber}
          onChange={(e) => setDoorNumber(e.target.value)}
          type="number"
          sx={{ mr: 2 }}
        />
        <Button variant="contained" onClick={handleOpenDoor}>
          Open Door
        </Button>
      </Paper>

      {/* 2) Show Total Permissions */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6">Show Total Permissions</Typography>
        <Button variant="contained" onClick={handleGetTotalPermissions}>
          Get Total Permissions
        </Button>
      </Paper>

      {/* 3) Add Permission */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6">Add Permission</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Card Number"
              value={cardNumber}
              onChange={(e) => setCardNumber(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="User Name"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Door Flags (e.g., 1 0 1 0)"
              value={doorFlags}
              onChange={(e) => setDoorFlags(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Expiry Date (yyyy-MM-dd)"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" onClick={handleAddPermission}>
              Add Permission
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* 4) Delete All Permissions */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6">Delete All Permissions</Typography>
        <Button variant="contained" onClick={handleDeleteAllPermissions}>
          Delete All Permissions
        </Button>
      </Paper>

      {/* 5) Delete Single Permission */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6">
          Delete Single Permission (by Card Number)
        </Typography>
        <TextField
          label="Card Number"
          value={cardNumber}
          onChange={(e) => setCardNumber(e.target.value)}
          sx={{ mr: 2 }}
        />
        <Button variant="contained" onClick={handleDeletePermission}>
          Delete Permission
        </Button>
      </Paper>

      {/* 6) Get Last Swiped Card */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6">Get Last Swiped Card</Typography>
        <Button variant="contained" onClick={handleGetLastSwipedCard}>
          Get Last Swiped Card
        </Button>
      </Paper>

      {/* 7) Sync Date Time */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6">Sync Date/Time</Typography>
        <Button variant="contained" onClick={handleSyncDateTime}>
          Sync Date/Time
        </Button>
      </Paper>

      {/* 8) Read Name By Card */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6">Read Name By Card</Typography>
        <TextField
          label="Card Number"
          value={cardNumber}
          onChange={(e) => setCardNumber(e.target.value)}
          sx={{ mr: 2 }}
        />
        <Button variant="contained" onClick={handleReadNameByCard}>
          Read Name By Card
        </Button>
      </Paper>

      {/* 9) Read Name By Index */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6">Read Name By Index</Typography>
        <TextField
          label="Index Number"
          value={indexNumber}
          onChange={(e) => setIndexNumber(e.target.value)}
          sx={{ mr: 2 }}
        />
        <Button variant="contained" onClick={handleReadNameByIndex}>
          Read Name By Index
        </Button>
      </Paper>

      {/* 10) Get All Record Swipes */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6">Get All Record Swipes</Typography>
        <Button variant="contained" onClick={handleGetAllRecordSwipes}>
          Get All Record Swipes
        </Button>
      </Paper>

      {/* 11) View Permissions for a Card */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6">View Permissions for a Card</Typography>
        <TextField
          label="Card Number"
          value={cardNumber}
          onChange={(e) => setCardNumber(e.target.value)}
          sx={{ mr: 2 }}
        />
        <Button variant="contained" onClick={handleViewPermissions}>
          View Permissions
        </Button>
      </Paper>

      {/* 12) Search Controllers */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6">Search Controllers</Typography>
        <Button variant="contained" onClick={handleSearchControllers}>
          Search Controllers
        </Button>
      </Paper>

      {/* 13) Put Card (Add/Update) */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6">Put Card (Add/Update Card)</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Card Number"
              value={putCardNumber}
              onChange={(e) => setPutCardNumber(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="User Name"
              value={putUserName}
              onChange={(e) => setPutUserName(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Valid From (yyyy-MM-dd HH:mm:ss)"
              value={putFrom}
              onChange={(e) => setPutFrom(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Valid To (yyyy-MM-dd HH:mm:ss)"
              value={putTo}
              onChange={(e) => setPutTo(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Door Flags (e.g., 1 0 1 0)"
              value={putDoorFlags}
              onChange={(e) => setPutDoorFlags(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="PIN"
              value={putPin}
              onChange={(e) => setPutPin(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button variant="contained" onClick={handlePutCard}>
              Put Card
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* 14) Get Card & Name */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6">Get Card & Name</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Card Number"
              value={getCardNumber}
              onChange={(e) => setGetCardNumber(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button variant="contained" onClick={handleGetCardAndName}>
              Get Card + Name
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* 15) Supervisor Passcodes (4 fields) */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6">Supervisor Passcodes</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Door Number"
              value={supervisorDoorNumber}
              onChange={(e) => setSupervisorDoorNumber(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Passcode #1"
              value={supervisorPass1}
              onChange={(e) => setSupervisorPass1(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Passcode #2"
              value={supervisorPass2}
              onChange={(e) => setSupervisorPass2(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Passcode #3"
              value={supervisorPass3}
              onChange={(e) => setSupervisorPass3(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Passcode #4"
              value={supervisorPass4}
              onChange={(e) => setSupervisorPass4(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={9}>
            <Button
              variant="contained"
              onClick={handleSetSupervisorPasscodes}
              sx={{ mr: 2 }}
            >
              Set Supervisor Passcodes
            </Button>
            <Button variant="outlined" onClick={handleClearSupervisorPasscodes}>
              Clear Supervisor Passcodes
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <SupervisorPasscodeManagerAutoClear />

      {/* Response Display */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6">Response</Typography>
        <Box
          component="pre"
          sx={{ backgroundColor: "#f4f4f4", p: 2, whiteSpace: "pre-wrap" }}
        >
          {result}
        </Box>
      </Paper>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="info"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default AccessControl;
